import { useState, useEffect, useCallback } from 'react';
import { Wrench, AlertTriangle, RefreshCw, CheckCircle2, Search, History, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import api from '@/services/api';

const inr = (v) => v == null ? '—' : `₹${Math.round(Number(v) || 0).toLocaleString('en-IN')}`;
const pct = (v) => v == null ? '—' : `${Number(v).toFixed(2)}%`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
const diffCell = (a, b) => {
  const aNum = Number(a); const bNum = Number(b);
  const changed = !isNaN(aNum) && !isNaN(bNum) ? Math.abs(aNum - bNum) > 0.5 : a !== b;
  return changed;
};

export default function FixIssues() {
  const { toast } = useToast();
  const [tab, setTab] = useState('products-pricing');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [onlyDrift, setOnlyDrift] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [applying, setApplying] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [log, setLog] = useState([]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/pricing-fix/products', {
        params: { only_drift: onlyDrift, q: query || undefined, limit: 300 },
      });
      setRows(res.data?.items || []);
    } catch (e) {
      toast({ title: 'Load failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [onlyDrift, query, toast]);

  const fetchLog = useCallback(async () => {
    try {
      const res = await api.get('/pricing-fix/log', { params: { limit: 100 } });
      setLog(res.data?.items || []);
    } catch (e) {
      console.error('log fetch failed', e);
    }
  }, []);

  useEffect(() => { fetchRows(); fetchLog(); }, [fetchRows, fetchLog]);

  const toggleSelect = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const toggleSelectAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map(r => r.product_id)));
  };

  const openPreview = async (productId) => {
    try {
      const res = await api.get(`/pricing-fix/products/${productId}/preview`);
      setPreviewData(res.data);
      setPreviewOpen(true);
    } catch (e) {
      toast({ title: 'Preview failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const applyOne = async (productId) => {
    setApplying(true);
    try {
      const res = await api.post(`/pricing-fix/products/${productId}/reapply`);
      if (res.data?.applied) {
        toast({ title: 'Fixed ✓', description: `Re-applied latest GRN pricing (${Object.keys(res.data?.entry?.drift || {}).length} field(s))` });
      } else {
        toast({ title: 'Already in sync', description: res.data?.message || 'Nothing to fix.' });
      }
      await fetchRows();
      await fetchLog();
      setPreviewOpen(false);
    } catch (e) {
      toast({ title: 'Apply failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setApplying(false);
    }
  };

  const applyBulk = async () => {
    if (selected.size === 0) return;
    setApplying(true);
    try {
      const res = await api.post('/pricing-fix/products/bulk-reapply', {
        product_ids: Array.from(selected),
      });
      const fixed = (res.data?.results || []).filter(r => r.applied).length;
      toast({
        title: `Bulk fix done`,
        description: `${fixed} fixed · ${res.data?.failed || 0} failed · ${res.data?.total - fixed} unchanged`,
      });
      setSelected(new Set());
      await fetchRows();
      await fetchLog();
    } catch (e) {
      toast({ title: 'Bulk apply failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="fix-issues-page">
      <Toaster />
      <div className="flex items-center gap-3">
        <Wrench className="w-6 h-6 text-amber-600" />
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Fix Issues</h1>
          <p className="text-sm text-gray-500">Reconcile data drift across the ERP without touching source transactions.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="products-pricing" data-testid="tab-products-pricing">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Products Pricing Issues Fix
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <History className="w-4 h-4 mr-2" />
            Fix History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="products-pricing" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Pricing drift vs latest approved GRN</CardTitle>
              <CardDescription>
                Products whose Master pricing (Base Cost, SP1, markdown/markup %) doesn't match
                the latest approved GRN. Re-apply to pull the GRN's values onto the Product Master.
                Stock and ledgers are not touched.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Search by SKU or product name"
                    className="pl-9"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchRows()}
                    data-testid="fix-search-input"
                  />
                </div>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Checkbox
                    checked={onlyDrift}
                    onCheckedChange={(v) => setOnlyDrift(!!v)}
                    data-testid="only-drift-toggle"
                  />
                  Only show drifted
                </label>
                <Button variant="outline" onClick={fetchRows} disabled={loading} data-testid="refresh-btn">
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                {selected.size > 0 && (
                  <Button onClick={applyBulk} disabled={applying} className="bg-amber-600 hover:bg-amber-700" data-testid="bulk-apply-btn">
                    {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                    Fix {selected.size} selected
                  </Button>
                )}
              </div>

              <div className="rounded-md border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={rows.length > 0 && selected.size === rows.length}
                          onCheckedChange={toggleSelectAll}
                          data-testid="select-all"
                        />
                      </TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Last GRN</TableHead>
                      <TableHead className="text-right">Master Base Cost</TableHead>
                      <TableHead className="text-right">GRN Base Cost</TableHead>
                      <TableHead className="text-right">Master SP1</TableHead>
                      <TableHead className="text-right">GRN SP1</TableHead>
                      <TableHead className="text-right">Master %</TableHead>
                      <TableHead className="text-right">GRN %</TableHead>
                      <TableHead className="text-center">Drift</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading && (
                      <TableRow><TableCell colSpan={11} className="text-center text-gray-400 py-6">Loading…</TableCell></TableRow>
                    )}
                    {!loading && rows.length === 0 && (
                      <TableRow><TableCell colSpan={11} className="text-center text-gray-400 py-6">
                        {onlyDrift ? 'All products are in sync with their latest GRN.' : 'No products found.'}
                      </TableCell></TableRow>
                    )}
                    {rows.map(r => (
                      <TableRow key={r.product_id} data-testid={`row-${r.product_id}`}>
                        <TableCell>
                          <Checkbox
                            checked={selected.has(r.product_id)}
                            onCheckedChange={() => toggleSelect(r.product_id)}
                            data-testid={`select-${r.product_id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="font-medium text-gray-900">{r.name}</div>
                          <div className="text-xs text-gray-500">{r.sku}</div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm font-mono text-teal-700">{r.grn_number || '—'}</div>
                          <div className="text-xs text-gray-400">{fmtDate(r.approved_at)}</div>
                        </TableCell>
                        <TableCell className={`text-right ${diffCell(r.master_base_cost, r.grn_base_cost) ? 'text-amber-600 font-semibold' : ''}`}>
                          {inr(r.master_base_cost)}
                        </TableCell>
                        <TableCell className="text-right">{inr(r.grn_base_cost)}</TableCell>
                        <TableCell className={`text-right ${diffCell(r.master_sp1, r.grn_sp1) ? 'text-amber-600 font-semibold' : ''}`}>
                          {inr(r.master_sp1)}
                        </TableCell>
                        <TableCell className="text-right">{inr(r.grn_sp1)}</TableCell>
                        <TableCell className={`text-right ${diffCell(r.master_pricing_percent, r.grn_pricing_percent) ? 'text-amber-600 font-semibold' : ''}`}>
                          {pct(r.master_pricing_percent)}
                        </TableCell>
                        <TableCell className="text-right">{pct(r.grn_pricing_percent)}</TableCell>
                        <TableCell className="text-center">
                          {r.drift_count > 0 ? (
                            <Badge variant="outline" className="border-amber-400 text-amber-700 bg-amber-50">
                              {r.drift_count} field{r.drift_count > 1 ? 's' : ''}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="border-emerald-400 text-emerald-700 bg-emerald-50">
                              In sync
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-2 justify-end">
                            <Button variant="ghost" size="sm" onClick={() => openPreview(r.product_id)} data-testid={`preview-${r.product_id}`}>
                              Preview
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => applyOne(r.product_id)}
                              disabled={applying || r.drift_count === 0}
                              className="bg-amber-600 hover:bg-amber-700"
                              data-testid={`fix-${r.product_id}`}
                            >
                              Fix
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Fix History</CardTitle>
              <CardDescription>Audit trail of every pricing fix applied, with before/after values.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>GRN</TableHead>
                      <TableHead>Fixed By</TableHead>
                      <TableHead>Fields Changed</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {log.length === 0 && (
                      <TableRow><TableCell colSpan={5} className="text-center text-gray-400 py-6">No fixes applied yet.</TableCell></TableRow>
                    )}
                    {log.map(entry => (
                      <TableRow key={entry.id} data-testid={`log-${entry.id}`}>
                        <TableCell className="text-xs">{fmtDate(entry.fixed_at)}</TableCell>
                        <TableCell>
                          <div className="font-medium">{entry.product_name}</div>
                          <div className="text-xs text-gray-500">{entry.sku}</div>
                        </TableCell>
                        <TableCell className="font-mono text-teal-700 text-sm">{entry.grn_number}</TableCell>
                        <TableCell className="text-sm">{entry.fixed_by}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(entry.drift || {}).map(([field, change]) => (
                              <Badge key={field} variant="outline" className="font-mono text-xs">
                                {field}: {JSON.stringify(change.before)} → {JSON.stringify(change.after)}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Preview Fix</DialogTitle>
          </DialogHeader>
          {previewData && (
            <div className="space-y-3">
              <div className="text-sm">
                <div className="font-medium text-gray-900">{previewData.product?.name}</div>
                <div className="text-xs text-gray-500 font-mono">{previewData.product?.sku}</div>
                <div className="text-xs text-teal-700 mt-1">
                  Latest GRN: {previewData.latest_grn?.grn_number} · {fmtDate(previewData.latest_grn?.approved_at)}
                </div>
              </div>
              <div className="rounded-md border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Field</TableHead>
                      <TableHead>Before (Master)</TableHead>
                      <TableHead>After (from GRN)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.keys(previewData.drift || {}).length === 0 && (
                      <TableRow><TableCell colSpan={3} className="text-center text-emerald-600 py-4">Already in sync — nothing to fix.</TableCell></TableRow>
                    )}
                    {Object.entries(previewData.drift || {}).map(([field, change]) => (
                      <TableRow key={field}>
                        <TableCell className="font-mono text-xs">{field}</TableCell>
                        <TableCell className="text-gray-500 text-sm">{JSON.stringify(change.before)}</TableCell>
                        <TableCell className="text-emerald-700 text-sm">{JSON.stringify(change.after)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Cancel</Button>
            <Button
              onClick={() => previewData && applyOne(previewData.product.id)}
              disabled={applying || !previewData || Object.keys(previewData?.drift || {}).length === 0}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="preview-apply-btn"
            >
              {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Apply Fix
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
